import { useEffect, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import cn from 'classnames';
import { Icon, type IconPath } from '@nespresso-design-system/components';
import styles from './Action.module.scss';
import { useIntersection } from '@/widgets/hooks/useIntersection';

export type ActionProps = {
  children: ComponentChildren;
  onClick: () => void;
  onImpression?: () => void;
  disabled?: boolean;
  class?: string;
};

export default function Action({
  children,
  onClick,
  onImpression,
  disabled,
  class: className
}: ActionProps) {
  const { elementRef, isVisible } = useIntersection<HTMLButtonElement>();
  const impressionSent = useRef(false);

  useEffect(() => {
    if (isVisible && !impressionSent.current && onImpression) {
      onImpression();
      impressionSent.current = true;
    }
  }, [isVisible, onImpression]);

  return (
    <button
      ref={elementRef}
      type="button"
      class={cn(styles.button, className)}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

type IconProps = {
  /** Like: 24/order/promotions */
  icon: string;
};

function ActionIcon({ icon }: IconProps) {
  return <Icon icon={icon as IconPath} />;
}

function ActionCtaIcon({ icon }: IconProps) {
  return (
    <Icon
      icon={icon as IconPath}
      style={{
        width: 15,
        height: 15
      }}
    />
  );
}

type ActionIconLabelProps = {
  label: string;
  class?: string;
};

function ActionIconLabel({ label, class: className }: ActionIconLabelProps) {
  return <span class={cn(styles.iconLabel, className)}>{label}</span>;
}

type ActionCtaLabelProps = {
  label: string;
  class?: string;
};

function ActionCtaLabel({ label, class: className }: ActionCtaLabelProps) {
  return <span class={cn(styles.ctaLabel, className)}>{label}</span>;
}

Action.Icon = ActionIcon;
Action.IconLabel = ActionIconLabel;
Action.CtaIcon = ActionCtaIcon;
Action.CtaLabel = ActionCtaLabel;
